import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogContent,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import NewReleasesOutlinedIcon from "@material-ui/icons/NewReleasesOutlined";

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: 8,
    height: "85vh",
    display: "flex",
    flexDirection: "column",
    [theme.breakpoints.down("sm")]: {
      height: "92vh",
      margin: theme.spacing(1),
    },
  },
  header: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(2.5, 3, 2),
    gap: theme.spacing(2),
  },
  headerIcon: {
    ...theme.neoUI?.buttons?.action?.root,
    width: 40,
    height: 40,
    borderRadius: theme.shape.borderRadius,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    flexShrink: 0,
    "&:hover": {
      ...theme.neoUI?.buttons?.action?.root?.["&:hover"],
    },
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 600,
    color: theme.palette.text.primary,
    flex: 1,
    minWidth: 0,
  },
  headerCloseButton: {
    ...theme.neoUI?.buttons?.action?.cancel?.root,
    height: 32,
    flexShrink: 0,
  },
  content: {
    padding: theme.spacing(0, 3, 2.5),
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
  },
  versionNav: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0, 3, 2),
    overflowX: "auto",
  },
  versionNavButton: {
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.875rem",
    minHeight: 32,
    height: 32,
    minWidth: 0,
    padding: theme.spacing(0, 1.5),
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.primary,
    backgroundColor: "transparent",
    flexShrink: 0,
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
      borderColor: theme.palette.primary.main,
    },
  },
  versionNavButtonSelected: {
    ...theme.neoUI?.buttons?.action?.root,
    fontWeight: 600,
    minHeight: 32,
    height: 32,
    flexShrink: 0,
  },
  moreVersionsButton: {
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.875rem",
    minHeight: 32,
    height: 32,
    padding: theme.spacing(0, 1.5),
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.primary,
    backgroundColor: "transparent",
    flexShrink: 0,
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
      borderColor: theme.palette.primary.main,
    },
  },
  summary: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  releaseTitle: {
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
  },
  section: {
    marginTop: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 700,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(0.75),
  },
  sectionList: {
    margin: 0,
    paddingLeft: theme.spacing(2.5),
    color: theme.palette.text.primary,
    "& li": {
      marginBottom: theme.spacing(0.75),
      lineHeight: 1.45,
    },
  },
  emptyText: {
    color: theme.palette.text.secondary,
    fontStyle: "italic",
  },
  versionButton: {
    fontSize: 12,
    fontWeight: 600,
    color: "inherit",
    borderRadius: 4,
    padding: theme.spacing(0.25, 0.75),
    lineHeight: 1.4,
    "&:hover": {
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.action.hover,
    },
    "&:focus-visible": {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
  },
}));

const SECTION_CONFIG = [
  ["highlights", "Destaques"],
  ["improvements", "Melhorias"],
  ["fixes", "Correções"],
];

const ReleaseSection = ({ title, items }) => {
  const classes = useStyles();
  const safeItems = Array.isArray(items) ? items : [];

  return (
    <Box className={classes.section}>
      <Typography className={classes.sectionTitle}>{title}</Typography>
      {safeItems.length > 0 ? (
        <ul className={classes.sectionList}>
          {safeItems.map((item, index) => (
            <li key={`${title}-${index}`}>{item}</li>
          ))}
        </ul>
      ) : (
        <Typography variant="body2" className={classes.emptyText}>
          Sem registros públicos nesta seção.
        </Typography>
      )}
    </Box>
  );
};

const ReleaseNotesModal = ({ open, onClose, releaseInfo }) => {
  const classes = useStyles();
  const info = releaseInfo || {};
  const [moreAnchorEl, setMoreAnchorEl] = useState(null);
  const releases = useMemo(() => {
    const current = info.version
      ? [{
          version: info.version,
          date: info.date,
          title: info.title,
          sections: info.sections || {},
        }]
      : [];
    const history = Array.isArray(info.history) ? info.history : [];

    return [...current, ...history];
  }, [info]);
  const [selectedVersion, setSelectedVersion] = useState(info.version);
  const selectedRelease = releases.find((entry) => entry.version === selectedVersion) || releases[0] || {};
  const sections = selectedRelease.sections || {};
  const visibleReleases = releases.slice(0, 8);
  const hiddenReleases = releases.slice(8);

  const handleSelectVersion = (version) => {
    setSelectedVersion(version);
    setMoreAnchorEl(null);
  };

  useEffect(() => {
    if (open) {
      setSelectedVersion(info.version);
      setMoreAnchorEl(null);
    }
  }, [info.version, open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      classes={{ paper: classes.dialogPaper }}
      aria-labelledby="release-notes-title"
    >
      <Box className={classes.header}>
        <Box className={classes.headerIcon}>
          <NewReleasesOutlinedIcon fontSize="small" />
        </Box>
        <Typography id="release-notes-title" className={classes.headerTitle}>
          Novidades da versão {selectedRelease.version || ""}
        </Typography>
        <Button
          className={classes.headerCloseButton}
          onClick={onClose}
          variant="outlined"
        >
          Fechar
        </Button>
      </Box>

      {releases.length > 0 ? (
        <Box className={classes.versionNav}>
          {visibleReleases.map((entry) => (
            <Button
              key={`${entry.version}-${entry.title}`}
              className={
                entry.version === selectedRelease.version
                  ? classes.versionNavButtonSelected
                  : classes.versionNavButton
              }
              onClick={() => handleSelectVersion(entry.version)}
              variant={entry.version === selectedRelease.version ? "contained" : "outlined"}
            >
              {entry.version}
            </Button>
          ))}
          {hiddenReleases.length > 0 ? (
            <>
              <Button
                className={classes.moreVersionsButton}
                onClick={(event) => setMoreAnchorEl(event.currentTarget)}
                variant="outlined"
              >
                Mais versões
              </Button>
              <Menu
                anchorEl={moreAnchorEl}
                open={Boolean(moreAnchorEl)}
                onClose={() => setMoreAnchorEl(null)}
                getContentAnchorEl={null}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                {hiddenReleases.map((entry) => (
                  <MenuItem
                    key={`${entry.version}-${entry.title}`}
                    selected={entry.version === selectedRelease.version}
                    onClick={() => handleSelectVersion(entry.version)}
                  >
                    {entry.version} - {entry.title}
                  </MenuItem>
                ))}
              </Menu>
            </>
          ) : null}
        </Box>
      ) : null}

      <DialogContent dividers className={classes.content}>
        <Box className={classes.summary}>
          <Typography className={classes.releaseTitle}>
            {selectedRelease.title || "Atualização do NeoZapp"}
          </Typography>
          {selectedRelease.date ? (
            <Typography variant="body2">Publicado em {selectedRelease.date}</Typography>
          ) : null}
        </Box>

        {SECTION_CONFIG.map(([key, label]) => (
          <ReleaseSection key={key} title={label} items={sections[key]} />
        ))}
      </DialogContent>
    </Dialog>
  );
};

// `hidden` removes only the button: the notes stay open when the menu around it collapses.
export const ReleaseVersionButton = ({ releaseInfo, className, hidden, logo }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const version = releaseInfo?.version || "0.0.0";

  return (
    <>
      {!hidden && (
        <Tooltip title="Ver novidades da versão" placement="top">
          <ButtonBase
            className={className || classes.versionButton}
            onClick={() => setOpen(true)}
            aria-label={`Ver novidades da versão ${version}`}
            style={logo ? { gap: 8 } : undefined}
          >
            v{version}
            {logo && <img src={logo} alt="" style={{ display: "block", width: 22, height: 22 }} />}
          </ButtonBase>
        </Tooltip>
      )}
      <ReleaseNotesModal
        open={open}
        onClose={() => setOpen(false)}
        releaseInfo={releaseInfo}
      />
    </>
  );
};

export default ReleaseNotesModal;
